import { useState, useMemo, useRef, useEffect, useContext } from 'react';
import {
  Box,
  Modal,
  Button,
  Stack,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { toast } from 'react-toastify';
import LockIconWrapper from '../../components/LockIconWrapper';
import DateSelect from './components/DateSelect';
import TimeSelect from './components/TimeSelect';
import PersonSelect from './components/PersonSelect';
import PackageAndUpsellSelect from './components/PackageAndUpsellSelect';
import CustomerData from './components/CustomerData';
import Overview from './components/Overview';
import EntertainmentSelect from './components/EntertainmentSelect';
import CopyCustomerDataSelect from './components/CopyCustomerDataSelect';
import { useBooking } from '../../../context/bookingContext';
import { useAuth } from '../../../context/authContext';
import SocketIOContext from '../../../context/socketIOContext';
import dayjs from 'dayjs';
import DurationSelect from './components/DurationSelect';
import useTemporaryResevation from '../../../hooks/useTemporaryResevation';
import useInternalBooking from '../../../hooks/useInternalBooking';
import useWalkInSubmissions from '../../../hooks/useWalkInSubmissions';
import MailDeliveryAnimation from '../../emails/MailDeliveryAnimation';

export default function CreateBookingModal({
  open,
  onClose,
  onSuccess,
  onAfterCreate,
  branchId = null,
  entertainmentId = null,
}) {
  const {
    selectedDate,
    setSelectedDate,
    setPreviousSelectedDate,
    selectedTime,
    setSelectedTime,
    selectedDuration,
    setSelectedDuration,
    selectedCategories,
    setSelectedCategories,
    selectedPackage,
    setSelectedPackage,
    selectedUpsells,
    setSelectedUpsells,
    selectedBranch,
    setSelectedBranch,
    selectedEntertainment,
    setSelectedEntertainment,
    selectedEntertainmentObject,
    setSelectedEntertainmentObject,
    selectedProduct,
    setSelectedProduct,
    selectedResource,
    setSelectedResource,
    selectedPricingId,
    setSelectedPricingId,
    lineItems,
    entertainmentError,
    setEntertainmentError,
    tempReservationId,
    setTempReservationId,
    setCarryOverQuantities,
    setPackageTags,
    customerData,
    calculateTotalAmount,
    resetBooking,
    priceOverrides,
    setPriceOverrides,
    setSpecialLineItems,
    overbooking,
    setOverbooking,
    timeTags,
    setTimeTags,
    packageTags,
    getCurrentTags,
    fetchPackages,
    fetchUpsells,
    calculateLineItems,
    specialLineItems,
    areSpecialLineItemsComplete,
    isUpsellsLoading,
    isPackagesLoading,
    resources,
    packages,
    setPackages,
    upsells,
    setUpsells,
  } = useBooking();

  const {
    addTemporaryReservation,
    updatePackageOfTemporaryReservation,
    updateUpsellsOfTemporaryReservation,
    invalidateTemporaryReservation,
  } = useTemporaryResevation();
  const {
    convertTemporaryReservationToBooking,
    convertCartToBookings,
    getCustomerConfirmationMailStatus,
    resendBookingConfirmation,
    updateInternalBooking,
  } = useInternalBooking();
  const { deleteWalkInSubmission } = useWalkInSubmissions();
  const { checkRightAny, preferences } = useAuth();
  const showMailDeliveryStep = preferences?.showMailDeliveryStep !== false;
  const { isConnected, emit: socketEmit, on: socketOn } = useContext(SocketIOContext) || {};
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showRequestOnlyConfirm, setShowRequestOnlyConfirm] = useState(false);
  const [currentStep, setCurrentStep] = useState('booking');
  const [isDayBookable, setIsDayBookable] = useState(true);
  const [createdBookingId, setCreatedBookingId] = useState(null);
  const [mailDeliveryStatus, setMailDeliveryStatus] = useState(null);
  const [mailStatusPollCount, setMailStatusPollCount] = useState(0);
  const [isMailStatusLoading, setIsMailStatusLoading] = useState(false);
  const [isResendingMail, setIsResendingMail] = useState(false);
  const [correctedMail, setCorrectedMail] = useState('');
  const [mailCorrectionError, setMailCorrectionError] = useState('');
  const [reservationCartSummary, setReservationCartSummary] = useState(null);
  // Parked reservations of the cart ("Warenkorb"): further entertainments of the
  // same branch the staff member added before completing everything as one
  // grouped booking with a shared meta booking number.
  const [cartReservations, setCartReservations] = useState([]);
  // Bumped when a parked cart item is re-opened for editing: it remounts the
  // booking step so its components start from the restored selection.
  const [bookingStepKey, setBookingStepKey] = useState(0);
  const overviewRef = useRef(null);
  const customerDataRef = useRef(null);
  // Id of a pre-submitted walk-in entry the staff member took over, if any.
  // Deleted once the booking is completed (the backend TTL is the backstop).
  const selectedWalkInSubmissionIdRef = useRef(null);
  const reservationSnapshotRef = useRef(null);
  // The cart item currently re-opened in the wizard, as long as its selection has
  // not been re-confirmed. Its temporary reservation still describes the stored
  // state, so an unconfirmed change must not be parked as if it were reserved.
  const editingCartItemRef = useRef(null);
  const restoredProductRef = useRef(null);
  const createdBookingFollowUpRef = useRef({ shouldOpen: false, bookingId: null });
  const getCustomerConfirmationMailStatusRef = useRef(getCustomerConfirmationMailStatus);

  const buildReservationSnapshot = () => {
    const categories = (selectedCategories || [])
      .map(category => ({
        name: category.name,
        count: parseInt(String(category.quantity), 10) || 0,
      }))
      .filter(c => c.count > 0)
      .sort((a, b) => a.name.localeCompare(b.name));

    return {
      productId: selectedProduct,
      entertainmentId: selectedEntertainment,
      resourceId: selectedResource || null,
      dateOfEvent: selectedDate?.format?.('YYYY-MM-DD') ?? null,
      categories,
      pricingId: selectedPricingId,
      startTime: selectedTime,
      duration: selectedDuration,
      overbooking: overbooking,
    };
  };

  const isSnapshotEqual = (a, b) => {
    if (!a || !b) return false;
    return JSON.stringify(a) === JSON.stringify(b);
  };

  const invalidateReservationIfAny = async () => {
    if (!tempReservationId) return;
    try {
      await invalidateTemporaryReservation(tempReservationId);
    } catch (error) {
      toast.error('Fehler beim Stornieren der Reservierung');
      console.error('Error invalidating temporary reservation:', error);
    } finally {
      setTempReservationId(null);
      setReservationCartSummary(null);
      reservationSnapshotRef.current = null;
      editingCartItemRef.current = null;
    }
  };

  const getCreateReservationErrorToast = error => {
    const raw = String(error?.message || '').trim();
    if (!raw) return 'Fehler beim Erstellen der Reservierung';
    if (raw.includes('Failed to fetch')) return 'Backend nicht erreichbar';
    if (raw.startsWith('401')) return 'Sitzung abgelaufen – bitte erneut anmelden';
    if (raw.startsWith('403')) return 'Keine Berechtigung zum Erstellen der Reservierung';
    if (raw.startsWith('500')) return 'Fehler beim Erstellen der Reservierung (Backend)';
    return `Fehler beim Erstellen der Reservierung (${raw})`;
  };

  const isRequestOnly = useMemo(() => {
    const tags = getCurrentTags(false, true) || [];
    return Array.isArray(tags) && tags.includes('requiresEmployeeConfirmation');
  }, [getCurrentTags]);

  const hasConfirmPendingRight = useMemo(
    () => checkRightAny('internalBooking:special:confirmpending'),
    [checkRightAny]
  );

  // Key used to persist last customer data for quick reuse
  const LAST_CUSTOMER_DATA_KEY = 'fz:lastCustomerData';

  // Persist current customer data together with a timestamp
  const persistLastCustomerData = (data = customerData) => {
    try {
      const payload = { timestamp: Date.now(), data };
      localStorage.setItem(LAST_CUSTOMER_DATA_KEY, JSON.stringify(payload));
    } catch (err) {
      // Only log; UX should not be blocked if storage fails
      console.error('Failed to persist last customer data', err);
    }
  };
  // Reset time and duration when product changes — except when the product change
  // is the restore of a cart item being edited, which brings its own time and duration.
  useEffect(() => {
    const restoredProduct = restoredProductRef.current;
    restoredProductRef.current = null;
    if (restoredProduct !== null && restoredProduct === selectedProduct) return;
    setSelectedTime(null);
    setSelectedDuration(null);
  }, [selectedProduct]);

  useEffect(() => {
    getCustomerConfirmationMailStatusRef.current = getCustomerConfirmationMailStatus;
  }, [getCustomerConfirmationMailStatus]);

  useEffect(() => {
    if (currentStep !== 'mailDelivery' || !createdBookingId) return;
    const status = mailDeliveryStatus?.status;
    if (['delivered', 'failed', 'bounced', 'skipped'].includes(status)) return;

    let cancelled = false;
    const pollDelayMs = mailStatusPollCount === 0 ? 250 : Math.min(6500, 650 + mailStatusPollCount * 520);

    const timeout = window.setTimeout(
      async () => {
        setIsMailStatusLoading(true);
        try {
          const response = await getCustomerConfirmationMailStatusRef.current(createdBookingId);
          if (!cancelled) {
            setMailDeliveryStatus(response);
            setMailStatusPollCount(count => count + 1);
          }
        } catch (error) {
          if (!cancelled) {
            setMailDeliveryStatus({
              status: 'failed',
              reason: 'status_lookup_failed',
              mail: { errorMessage: 'Mailstatus konnte nicht abgerufen werden.' },
            });
          }
        } finally {
          if (!cancelled) {
            setIsMailStatusLoading(false);
          }
        }
      },
      pollDelayMs
    );

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [createdBookingId, currentStep, mailDeliveryStatus?.status, mailStatusPollCount]);

  useEffect(() => {
    if (
      currentStep !== 'mailDelivery' ||
      !createdBookingId ||
      !socketEmit ||
      typeof socketEmit !== 'function'
    ) {
      return undefined;
    }

    socketEmit('join_internal_booking_mail_status', {
      bookingId: createdBookingId,
    });
    return undefined;
  }, [createdBookingId, currentStep, socketEmit, isConnected]);

  useEffect(() => {
    if (
      currentStep !== 'mailDelivery' ||
      !createdBookingId ||
      !socketOn ||
      typeof socketOn !== 'function'
    ) {
      return undefined;
    }

    const unsubscribe = socketOn(
      'internalBooking:customerConfirmationMailStatus',
      payload => {
        if (!payload || String(payload.bookingId) !== String(createdBookingId)) return;
        setMailDeliveryStatus(current => ({
          ...(current || {}),
          ...payload,
        }));
      }
    );

    return typeof unsubscribe === 'function' ? unsubscribe : undefined;
  }, [createdBookingId, currentStep, socketOn]);

  const handleDateSelect = (date, dayData) => {
    if (!date) {
      setSelectedDate(null);
      setIsDayBookable(false);
      setSelectedTime(null);
      return;
    }
    const dateObj = dayjs(date);
    setSelectedDate(dateObj);
    setIsDayBookable(dayData ? dayData.isBookable : false);
    setSelectedTime(null);
  };

  const handleDurationSelect = duration => {
    setSelectedDuration(duration);
    setSelectedTime(null);
  };

  const handleTimeSelect = time => {
    setSelectedTime(time);
  };

  const handlePersonSelect = categories => {
    setSelectedCategories(categories);
  };

  // Special line items are only sent once description, count and price are filled in
  // (incomplete rows are drafts the staff member is still typing).
  const getCompleteSpecialLineItems = () => {
    const complete = specialLineItems.filter(
      item => item.description.trim() && item.count > 0 && item.price > 0
    );
    return complete.length > 0 ? complete : null;
  };

  // Sums the priced line items of a cart summary (voucher redemptions excluded).
  const sumCartSummaryTotal = summary => {
    if (!summary || !Array.isArray(summary.lineItems)) return null;
    return summary.lineItems
      .filter(item => item.type !== 'voucherRedemption')
      .reduce((sum, item) => sum + Number(item.price ?? 0) * Number(item.count ?? 0), 0);
  };

  // Snapshot of the current wizard selection, stored on the parked cart item so
  // the pencil in the cart panel can put the wizard back into exactly this item.
  const buildCartEditSnapshot = reservationSnapshot => ({
    branchId: selectedBranch,
    entertainmentId: selectedEntertainment,
    entertainmentObject: selectedEntertainmentObject,
    productId: selectedProduct,
    resourceId: selectedResource,
    dateOfEvent: selectedDate?.format?.('YYYY-MM-DD') ?? null,
    startTime: selectedTime,
    duration: selectedDuration,
    pricingId: selectedPricingId,
    overbooking,
    categories: (selectedCategories || []).map(category => ({ ...category })),
    packages: (packages || []).map(pkg => ({ ...pkg })),
    upsells: (upsells || []).map(upsell => ({ ...upsell })),
    selectedPackage: selectedPackage ? { ...selectedPackage } : null,
    selectedUpsells: (selectedUpsells || []).map(upsell => ({ ...upsell })),
    timeTags: [...(timeTags || [])],
    packageTags: [...(packageTags || [])],
    reservationSnapshot,
  });

  // Clears the selection belonging to a single cart item and returns to the
  // booking step. Branch, date and the customer data are kept for the next item.
  const clearCurrentItemSelection = () => {
    setPriceOverrides({});
    setSpecialLineItems([]);
    setTempReservationId(null);
    setReservationCartSummary(null);
    reservationSnapshotRef.current = null;
    editingCartItemRef.current = null;
    setSelectedTime(null);
    setSelectedDuration(null);
    setSelectedCategories([]);
    setSelectedPackage(null);
    setSelectedUpsells([]);
    setSelectedProduct(null);
    setSelectedPricingId(null);
    setSelectedResource(null);
    setSelectedEntertainment(null);
    setSelectedEntertainmentObject(null);
    setTimeTags([]);
    setPackageTags([]);
    setCurrentStep('booking');
  };

  // Parks the reservation currently in the wizard as a cart item and clears the
  // per-item selection. Price overrides and special line items belong to the
  // individual booking and are therefore parked alongside it. Returns the parked
  // item, or null when there is nothing to park or committing the upgrades failed.
  const parkCurrentReservation = async () => {
    if (!tempReservationId) return null;

    // A re-opened cart item whose selection was changed without confirming it via
    // "Pakete auswählen" still holds the temporary reservation of its stored
    // state. Parking the live selection would show a new date next to the old,
    // reserved price — and book the old one. The stored copy goes back instead.
    const editedItem = editingCartItemRef.current;
    if (
      editedItem &&
      editedItem.tempReservationId === tempReservationId &&
      !isSnapshotEqual(reservationSnapshotRef.current, buildReservationSnapshot())
    ) {
      setCartReservations(prev => [...prev, editedItem]);
      clearCurrentItemSelection();
      toast.warning(`Nicht bestätigte Änderung an ${editedItem.entertainmentName} wurde verworfen`);
      return editedItem;
    }

    // Parking from the packages step: persist the package/upsell selection on the
    // reservation first, exactly like continuing to the customer step would.
    let parkedCartSummary = reservationCartSummary;
    if (currentStep === 'packages') {
      const committed = await commitUpgradeSelection();
      if (committed === false) return null;
      parkedCartSummary = committed;
    }

    const entertainmentName =
      selectedEntertainmentObject?.longName ||
      selectedEntertainmentObject?.entertainmentName ||
      'Entertainment';
    const parkedSpecialLineItems = getCompleteSpecialLineItems();
    const summaryTotal = sumCartSummaryTotal(parkedCartSummary);
    const specialTotal = (parkedSpecialLineItems || []).reduce(
      (sum, item) => sum + Number(item.price || 0) * Number(item.count || 0),
      0
    );
    // On the overview step the Overview knows the exact payable total (vouchers,
    // fees); on earlier steps it is derived from the committed cart summary.
    const totalAmount =
      currentStep === 'overview'
        ? (overviewRef.current?.getPayableTotalAmount?.() ?? calculateTotalAmount())
        : summaryTotal !== null
          ? summaryTotal + specialTotal
          : calculateTotalAmount();

    const quantitiesByCategory = {};
    let personCount = 0;
    (selectedCategories || []).forEach(category => {
      const quantity = parseInt(String(category.quantity), 10) || 0;
      if (quantity > 0) {
        quantitiesByCategory[category.name] = quantity;
        personCount += quantity;
      }
    });

    // The counts come from the wizard's line items, so a package billed per
    // participant or per lane carries the same count the booking is priced with.
    const currentLineItems = lineItems || [];
    const packageLineItem = currentLineItems.find(item => item.type === 'package');
    const upsellLineItems = currentLineItems.filter(
      item => item.type === 'upsell' && item.count > 0
    );

    const parkedItem = {
      tempReservationId,
      entertainmentName,
      dateLabel: selectedDate
        ? `${selectedDate.format('DD.MM.YYYY')}${selectedTime ? ` ${selectedTime}` : ''}`
        : '',
      totalAmount,
      personCount,
      packageName: selectedPackage?.name ?? null,
      packageCount: packageLineItem?.count ?? (selectedPackage ? 1 : 0),
      upsells: upsellLineItems.map(item => ({ name: item.description, count: item.count })),
      cartSummary: parkedCartSummary,
      priceOverrides:
        priceOverrides && Object.keys(priceOverrides).length > 0 ? { ...priceOverrides } : null,
      specialLineItems: parkedSpecialLineItems,
      edit: buildCartEditSnapshot(reservationSnapshotRef.current),
    };

    setCartReservations(prev => [...prev, parkedItem]);

    // Pre-fill the person counts of the next entertainment with the parked ones.
    setCarryOverQuantities(
      Object.keys(quantitiesByCategory).length > 0 ? quantitiesByCategory : null
    );
    clearCurrentItemSelection();
    return parkedItem;
  };

  // Parks the current reservation in the cart and returns to the booking step so
  // another entertainment of the branch can be configured. The person counts are
  // kept as the pre-filled default for the next entertainment. Available from the
  // packages step onwards, so no customer data is needed before adding more items.
  const handleAddAnotherEntertainment = async () => {
    if (!tempReservationId || isLoading) return;
    const wasReopenedItem = editingCartItemRef.current?.tempReservationId === tempReservationId;
    const parked = await parkCurrentReservation();
    if (!parked) return;
    // An item that came out of the cart goes back into it — it was never "added".
    if (!wasReopenedItem) toast.info(`${parked.entertainmentName} in den Warenkorb gelegt`);
  };

  // Re-opens a parked cart item in the wizard: the item leaves the cart and its
  // complete selection (entertainment, product, room, date, time, persons,
  // package/upsells, price overrides) is restored, so it can be adjusted just like
  // when it was first booked. A reservation currently in the wizard is parked
  // first so nothing is lost. Its temporary reservation is kept — leaving the
  // selection untouched keeps it, changing anything recreates it on "Weiter".
  const handleEditCartReservation = async item => {
    if (isLoading) return;
    if (tempReservationId) {
      const wasReopenedItem = editingCartItemRef.current?.tempReservationId === tempReservationId;
      const parked = await parkCurrentReservation();
      if (!parked) return;
      if (!wasReopenedItem) toast.info(`${parked.entertainmentName} in den Warenkorb gelegt`);
    }

    const snapshot = item.edit;
    setCartReservations(prev =>
      prev.filter(entry => entry.tempReservationId !== item.tempReservationId)
    );

    const restoredDate = snapshot.dateOfEvent ? dayjs(snapshot.dateOfEvent) : null;
    const validDate = restoredDate && restoredDate.isValid() ? restoredDate : null;
    // Both date refs are set to the same day so the downstream steps do not treat
    // the restore as a date change and drop the duration/start time again.
    setSelectedDate(validDate);
    setPreviousSelectedDate(validDate);
    setIsDayBookable(true);
    if (snapshot.branchId) setSelectedBranch(snapshot.branchId);
    setSelectedEntertainment(snapshot.entertainmentId);
    setSelectedEntertainmentObject(snapshot.entertainmentObject);
    restoredProductRef.current = snapshot.productId;
    setSelectedProduct(snapshot.productId);
    setSelectedResource(snapshot.resourceId);
    setSelectedPricingId(snapshot.pricingId);
    setSelectedDuration(snapshot.duration);
    setSelectedTime(snapshot.startTime);
    setOverbooking(snapshot.overbooking);
    setSelectedCategories((snapshot.categories || []).map(category => ({ ...category })));
    setPackages((snapshot.packages || []).map(pkg => ({ ...pkg })));
    setUpsells((snapshot.upsells || []).map(upsell => ({ ...upsell })));
    setSelectedPackage(snapshot.selectedPackage ? { ...snapshot.selectedPackage } : null);
    setSelectedUpsells((snapshot.selectedUpsells || []).map(upsell => ({ ...upsell })));
    setTimeTags([...(snapshot.timeTags || [])]);
    setPackageTags([...(snapshot.packageTags || [])]);
    setPriceOverrides(item.priceOverrides ? { ...item.priceOverrides } : {});
    setSpecialLineItems(
      item.specialLineItems ? item.specialLineItems.map(lineItem => ({ ...lineItem })) : []
    );
    setTempReservationId(item.tempReservationId);
    setReservationCartSummary(item.cartSummary);
    reservationSnapshotRef.current = snapshot.reservationSnapshot || null;
    setEntertainmentError(null);

    // The person categories are re-fetched for the restored day, which resets the
    // counts. Handing them over as the carry-over quantities puts the parked
    // counts back in — the same mechanism the next cart item is pre-filled with.
    const restoredQuantities = {};
    (snapshot.categories || []).forEach(category => {
      const quantity = parseInt(String(category.quantity), 10) || 0;
      if (quantity > 0) restoredQuantities[category.name] = quantity;
    });
    setCarryOverQuantities(Object.keys(restoredQuantities).length > 0 ? restoredQuantities : null);

    // Remount the booking step so its components pick the restored selection up as
    // their initial state instead of treating it as a change and resetting it.
    setBookingStepKey(key => key + 1);
    setCurrentStep('booking');
    editingCartItemRef.current = item;
    toast.info(`${item.entertainmentName} wird bearbeitet`);
  };

  const handleRemoveCartReservation = async item => {
    setCartReservations(prev =>
      prev.filter(entry => entry.tempReservationId !== item.tempReservationId)
    );
    try {
      await invalidateTemporaryReservation(item.tempReservationId);
    } catch (error) {
      console.error('Error invalidating cart reservation:', error);
    }
  };

  const handleClose = async (invalidate = true) => {
    if (invalidate && tempReservationId) {
      try {
        await invalidateTemporaryReservation(tempReservationId);
      } catch (error) {
        toast.error('Fehler beim Stornieren der Reservierung');
        console.error('Error invalidating temporary reservation:', error);
      }
    }
    if (invalidate && cartReservations.length > 0) {
      await Promise.allSettled(
        cartReservations.map(item => invalidateTemporaryReservation(item.tempReservationId))
      );
    }
    setCartReservations([]);
    reservationSnapshotRef.current = null;
    editingCartItemRef.current = null;
    setCreatedBookingId(null);
    setMailDeliveryStatus(null);
    setMailStatusPollCount(0);
    setCorrectedMail('');
    setMailCorrectionError('');
    setCurrentStep('booking');
    createdBookingFollowUpRef.current = { shouldOpen: false, bookingId: null };
    resetBooking();
    onClose();
  };

  const finishCreatedBookingFlow = () => {
    const followUp = createdBookingFollowUpRef.current;
    handleClose(false);
    if (followUp.shouldOpen && onAfterCreate && followUp.bookingId) {
      onAfterCreate({ bookingId: followUp.bookingId });
    }
  };

  const handleCloseRequest = () => {
    if (currentStep === 'mailDelivery') {
      finishCreatedBookingFlow();
      return;
    }
    setShowConfirmClose(true);
  };

  const handleConfirmClose = () => {
    setShowConfirmClose(false);
    handleClose();
  };

  const handleCancelClose = () => {
    setShowConfirmClose(false);
  };

  const validateMailInput = value => {
    const normalized = String(value || '').trim();
    if (!normalized) return 'Bitte gib eine E-Mail-Adresse ein';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      return 'Bitte gib eine gültige E-Mail-Adresse ein';
    }
    return '';
  };

  const refreshMailDeliveryStatus = async () => {
    if (!createdBookingId) return null;
    setIsMailStatusLoading(true);
    try {
      const status = await getCustomerConfirmationMailStatus(createdBookingId);
      setMailDeliveryStatus(status);
      return status;
    } finally {
      setIsMailStatusLoading(false);
    }
  };

  const handleRetryMailDelivery = async () => {
    if (!createdBookingId || isResendingMail) return;
    const normalizedMail = correctedMail.trim();
    const validationError = validateMailInput(normalizedMail);
    if (validationError) {
      setMailCorrectionError(validationError);
      return;
    }

    setMailCorrectionError('');
    setIsResendingMail(true);
    try {
      const updateResponse = await updateInternalBooking(createdBookingId, { mail: normalizedMail });
      if (!updateResponse) return;
      await resendBookingConfirmation(createdBookingId);
      setMailDeliveryStatus({
        status: 'pending',
        reason: 'resent',
        recipient: normalizedMail,
        mail: null,
      });
      setMailStatusPollCount(0);
      toast.success('Bestätigungsmail wird erneut versendet');
    } catch (error) {
      toast.error('Fehler beim erneuten Versenden der Bestätigungsmail');
      console.error('Error resending booking confirmation:', error);
    } finally {
      setIsResendingMail(false);
    }
  };

  const handleGoToPackages = async () => {
    // Always tell the user which selections are missing instead of leaving the
    // button silently disabled.
    const missing = [];
    if (!selectedDate) missing.push('Datum');
    if (!selectedDuration) missing.push('Dauer');
    if (!selectedTime) missing.push('Uhrzeit');
    if (!selectedCategories || selectedCategories.length === 0) missing.push('Personenanzahl');
    if (missing.length > 0) {
      toast.error(`Bitte fülle aus: ${missing.join(', ')}`);
      return;
    }

    // From here on the reservation matches the live selection (it is either
    // unchanged or recreated below), so a re-opened cart item is no longer parked
    // from its stored copy.
    editingCartItemRef.current = null;

    const currentSnapshot = buildReservationSnapshot();
    const shouldRecreate =
      !tempReservationId ||
      !reservationSnapshotRef.current ||
      !isSnapshotEqual(reservationSnapshotRef.current, currentSnapshot);

    if (shouldRecreate) {
      // The step is switched only once the reservation and its upgrades are
      // known. Switching to "packages" up front made the wizard show that step
      // and then jump on to the customer step for every product without
      // packages or upsells.
      setIsLoading(true);
      let createdReservationId = null;
      try {
        if (tempReservationId) {
          await invalidateReservationIfAny();
        }

        const reservationData = buildReservationSnapshot();
        setReservationCartSummary(null);

        const tempReservation = await addTemporaryReservation(reservationData);

        if (!tempReservation || !tempReservation.id) {
          throw new Error('Failed to create temporary reservation');
        }

        // Track the reservation before loading packages/upsells: if that load
        // fails we still know the id and can release the slot, instead of
        // leaving an orphaned reservation that blocks the capacity until it
        // expires.
        createdReservationId = tempReservation.id;
        setTempReservationId(tempReservation.id);
        reservationSnapshotRef.current = reservationData;

        setTimeTags(tempReservation.tags);
        const newTags = getCurrentTags(true, false).concat(tempReservation.tags);
        const [fetchedPackages, fetchedUpsells] = await Promise.all([
          fetchPackages(newTags),
          fetchUpsells(newTags),
        ]);

        // Skip packages step if no packages or upsells are available
        if (fetchedPackages.length === 0 && fetchedUpsells.length === 0) {
          setCurrentStep('customer');
        } else {
          setCurrentStep('packages');
        }
      } catch (error) {
        toast.error(getCreateReservationErrorToast(error));
        // Release a reservation that was already created before the failure so
        // the slot is not blocked for the retry.
        if (createdReservationId) {
          try {
            await invalidateTemporaryReservation(createdReservationId);
          } catch (invalidateError) {
            console.error('Error invalidating temporary reservation:', invalidateError);
          }
          setTempReservationId(null);
          reservationSnapshotRef.current = null;
        }
        return;
      } finally {
        setIsLoading(false);
      }
    } else {
      // For existing reservations, check if we should skip packages
      if (packages.length === 0 && upsells.length === 0) {
        setCurrentStep('customer');
      } else {
        setCurrentStep('packages');
      }
    }
  };

  // Persists the current package/upsell selection on the temporary reservation.
  // Returns the updated cart summary on success, false when validation or the
  // update failed (errors are already surfaced as toasts).
  const commitUpgradeSelection = async () => {
    if (!tempReservationId) {
      return false;
    }
    const sanitizedUpsells = [];
    const participantCount = selectedCategories.reduce((sum, category) => {
      const raw = category?.quantity ?? category?.count ?? 0;
      return sum + (parseInt(String(raw), 10) || 0);
    }, 0);
    const selectedPackageMinimum = Number(selectedPackage?.minimumCount);
    if (
      selectedPackage &&
      Number.isFinite(selectedPackageMinimum) &&
      selectedPackageMinimum >= 0 &&
      participantCount < Math.floor(selectedPackageMinimum)
    ) {
      toast.error(
        `Paket nicht waehlbar: mindestens ${Math.floor(selectedPackageMinimum)} Teilnehmer erforderlich.`
      );
      return false;
    }
    if (selectedUpsells && selectedUpsells.length > 0) {
      for (const upsell of selectedUpsells) {
        const rawQuantity = parseInt(upsell.quantity, 10);
        const quantity = Number.isFinite(rawQuantity) && rawQuantity > 0 ? rawQuantity : 0;
        const max =
          Number.isFinite(upsell.maximumCount) && upsell.maximumCount >= 0
            ? Math.floor(upsell.maximumCount)
            : null;

        if (max !== null && quantity > max) {
          toast.error(`Maximale Anzahl für ${upsell.name} ist ${max}.`);
          return false;
        }
        const minimum = Number(upsell.minimumCount);
        if (Number.isFinite(minimum) && minimum >= 0 && participantCount < Math.floor(minimum)) {
          toast.error(
            `${upsell.name} ist nicht waehlbar: mindestens ${Math.floor(minimum)} Teilnehmer erforderlich.`
          );
          return false;
        }

        if (quantity > 0) {
          sanitizedUpsells.push({ upsellId: upsell.id, count: quantity });
        }
      }
    }

    setIsLoading(true);
    try {
      const packageResponse = await updatePackageOfTemporaryReservation(
        tempReservationId,
        selectedPackage?.id || null
      );
      const upsellResponse = await updateUpsellsOfTemporaryReservation(tempReservationId, sanitizedUpsells);
      const summary = upsellResponse?.cartSummary || packageResponse?.cartSummary || null;
      setReservationCartSummary(summary);
      return summary;
    } catch (error) {
      const errorCode =
        error?.code || error?.response?.data?.code || error?.response?.code || error?.data?.code;
      if (errorCode === 'UPGRADE_MINIMUM_COUNT_NOT_REACHED') {
        toast.error(
          'Auswahl nicht möglich: Mindestteilnehmerzahl fuer Paket/Zusatzleistung unterschritten.'
        );
      } else {
        toast.error('Fehler beim Speichern von Paket/Zusatzleistungen');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToCustomer = async () => {
    const committed = await commitUpgradeSelection();
    if (committed === false) return;
    setCurrentStep('customer');
  };

  const performCompleteBooking = async (confirmIfPending = false) => {
    if (!tempReservationId) {
      return;
    }

    setIsLoading(true);

    try {
      const totalAmount = overviewRef.current?.getPayableTotalAmount?.() ?? calculateTotalAmount();
      // Staff can flag that the customer wants to pay online. The booking is then created as an
      // online booking and the backend mails a payment link for the customer frontend.
      const wantsOnlinePayment = overviewRef.current?.getOnlinePayment?.() || false;
      const paymentType = wantsOnlinePayment ? 'online' : 'offline';

      const formattedCustomerData = (() => {
        if (customerData?.isWalkIn) {
          // Only send minimal fields for walk-in
          return {
            firstName: customerData.firstName,
            lastName: customerData.lastName,
            mail: customerData.mail,
          };
        }
        if (customerData.birthdate) return customerData;
        const { birthdate, ...dataWithoutBirthdate } = customerData;
        return dataWithoutBirthdate;
      })();

      // filter out incomplete special line items (should never be necessary)
      const completeSpecialLineItems = getCompleteSpecialLineItems() || [];

      // Get comment, disableSelfService, suppressCustomerEmails and capacity overrides from Overview component
      const comment = overviewRef.current?.getComment() || '';
      const disableSelfService = overviewRef.current?.getDisableSelfService() || false;
      const suppressCustomerEmails = overviewRef.current?.getSuppressCustomerEmails?.() || false;
      const capacityParticipantCount = overviewRef.current?.getCapacityParticipantCount?.();
      const capacityLaneCount = overviewRef.current?.getCapacityLaneCount?.();

      const toastMessage = confirmIfPending
        ? `Die Anfrage, die Buchung für ${customerData.firstName} ${customerData.lastName} direkt zu bestätigen, wurde gespeichert und kann nach Identifizierung abgeschlossen werden`
        : isRequestOnly
          ? `Die Buchungsanfrage für ${customerData.firstName} ${customerData.lastName} wurde gespeichert und kann später bestätigt werden`
          : null;
      const successToastMessage = isRequestOnly
        ? confirmIfPending
          ? 'Buchung erfolgreich erstellt und bestätigt'
          : `Buchungsanfrage für ${customerData.firstName} ${customerData.lastName} gespeichert`
        : wantsOnlinePayment
          ? `Buchung für ${customerData.firstName} ${customerData.lastName} erstellt – Bezahllink wurde per E-Mail versendet`
          : `Buchung für ${customerData.firstName} ${customerData.lastName} erfolgreich erstellt`;

      const displayInfo = {
        Typ: confirmIfPending ? 'Buchung erstellen (Direkt bestätigen)' : 'Buchung',
        Kunde: `${customerData.firstName} ${customerData.lastName}`,
        Datum: selectedDate.format('DD.MM.YYYY'),
        Uhrzeit: selectedTime,
        Betrag: `${parseFloat(totalAmount).toFixed(2).replace('.', ',')}€`,
      };

      const onStored = () => {
        // Close the modal when the request is stored due to authentication failure
        handleClose(false);
      };

      const onSuccessCallback = response => {
        // A pre-submitted walk-in entry that was taken over is now consumed —
        // delete it (best-effort; the 20-min backend TTL is the backstop).
        if (selectedWalkInSubmissionIdRef.current) {
          const submissionId = selectedWalkInSubmissionIdRef.current;
          selectedWalkInSubmissionIdRef.current = null;
          deleteWalkInSubmission(submissionId).catch(() => {});
        }
        if (onSuccess) {
          onSuccess(response);
        }
        const shouldOpen = overviewRef.current?.getOpenAfterSave?.() || false;
        const newBookingId =
          response?.internalBooking?._id ||
          response?.internalBooking?.id ||
          response?.bookingId ||
          response?.booking?._id ||
          response?._id ||
          null;

        if (newBookingId && shouldOpen && onAfterCreate) {
          handleClose(false);
          onAfterCreate({ bookingId: newBookingId });
          return;
        }

        if (newBookingId && showMailDeliveryStep) {
          createdBookingFollowUpRef.current = { shouldOpen, bookingId: newBookingId };
          setCreatedBookingId(newBookingId);
          setCorrectedMail(customerData?.mail || '');
          setMailCorrectionError('');
          setMailDeliveryStatus(null);
          setMailStatusPollCount(0);
          setCurrentStep('mailDelivery');
          return;
        }

        handleClose(false);
      };

      if (cartReservations.length > 0) {
        // Every cart item keeps the price adjustments it was parked with; the item
        // currently on screen contributes the ones that are still in the form.
        const response = await convertCartToBookings({
          items: [
            ...cartReservations.map(item => ({
              temporaryReservationId: item.tempReservationId,
              priceOverrides: item.priceOverrides || null,
              specialLineItems: item.specialLineItems || null,
            })),
            {
              temporaryReservationId: tempReservationId,
              priceOverrides:
                priceOverrides && Object.keys(priceOverrides).length > 0 ? priceOverrides : null,
              specialLineItems:
                completeSpecialLineItems.length > 0 ? completeSpecialLineItems : null,
            },
          ],
          customerData: formattedCustomerData,
          comment,
          walkIn: Boolean(customerData?.isWalkIn),
          confirmIfPending,
          disableSelfService,
          suppressCustomerEmails,
        });

        if (response?.success) {
          toast.success(
            `Warenkorb-Buchung ${response.bookingGroup?.groupBookingNumber || ''} für ${customerData.firstName} ${customerData.lastName} erfolgreich erstellt`
          );
          setCartReservations([]);
          onSuccessCallback({
            internalBooking: Array.isArray(response.internalBookings)
              ? response.internalBookings[0]
              : null,
          });
        }
        return;
      }

      const response = await convertTemporaryReservationToBooking(
        tempReservationId,
        formattedCustomerData,
        totalAmount,
        paymentType,
        priceOverrides,
        completeSpecialLineItems,
        toastMessage,
        onStored,
        displayInfo,
        onSuccessCallback,
        successToastMessage,
        comment,
        Boolean(customerData?.isWalkIn),
        confirmIfPending,
        disableSelfService,
        capacityParticipantCount,
        capacityLaneCount,
        suppressCustomerEmails
      );

      if (response) {
        onSuccessCallback(response);
      }
    } catch (error) {
      console.error('Error completing booking:', error);
      // Only show error if it's not an authentication failure
      if (error.message !== 'INSUFFICIENT_RIGHTS') {
        toast.error('Fehler beim Abschluss der Buchung');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteBooking = async () => {
    if (isRequestOnly && !hasConfirmPendingRight) {
      await performCompleteBooking(false);
      return;
    }
    if (isRequestOnly && hasConfirmPendingRight) {
      setShowRequestOnlyConfirm(true);
      return;
    }
    await performCompleteBooking(false);
  };

  const handleDateError = error => {
    setEntertainmentError(error);
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedDuration(null);
    setSelectedCategories([]);
    setSelectedPackage(null);
    setSelectedUpsells([]);
  };

  const getMailDeliveryCopy = () => {
    const status = mailDeliveryStatus?.status || 'pending';
    if (status === 'delivered') {
      return {
        severity: 'success',
        title: 'Bestätigungsmail wurde zugestellt',
        text: 'Die Bestätigungsmail wurde erfolgreich an den Kunden zugestellt.',
      };
    }
    if (status === 'skipped') {
      return {
        severity: 'info',
        title: 'Es wurde keine Bestätigungsmail versendet',
        text:
          mailDeliveryStatus?.reason === 'suppressed_by_booking'
            ? 'Für diese Buchung wurden Kundenmails deaktiviert.'
            : 'Für diese Buchung ist keine verwendbare E-Mail-Adresse hinterlegt.',
      };
    }
    if (status === 'failed' || status === 'bounced') {
      const errorMessage =
        mailDeliveryStatus?.mail?.errorMessage ||
        mailDeliveryStatus?.bounceState?.lastErrorMessage ||
        'Die Adresse konnte nicht erreicht werden.';
      return {
        severity: 'error',
        title: 'Bestätigungsmail wurde nicht zugestellt',
        text: errorMessage,
      };
    }
    return {
      severity: 'info',
      title: 'Bestätigungsmail wird zugestellt',
      text: 'Die Buchung ist gespeichert. Wir warten auf die Bestätigung der Zustellung.',
    };
  };

  const renderMailAnimation = () => (
    <MailDeliveryAnimation status={mailDeliveryStatus?.status || 'pending'} />
  );

  const renderMailDeliveryContent = () => {
    const copy = getMailDeliveryCopy();
    const status = mailDeliveryStatus?.status || 'pending';
    const canCorrectMail = status === 'failed' || status === 'bounced';

    return (
      <Stack spacing={3} sx={{ alignItems: 'center', textAlign: 'center', py: { xs: 1, md: 2 } }}>
        {renderMailAnimation()}
        <Box>
          <Typography variant='h5' sx={{ fontWeight: 700, mb: 1 }}>
            {copy.title}
          </Typography>
          <Typography color='text.secondary' sx={{ maxWidth: 680 }}>
            {copy.text}
          </Typography>
        </Box>
        {mailDeliveryStatus?.recipient && (
          <Typography variant='body2' color='text.secondary'>
            Empfänger: <strong>{mailDeliveryStatus.recipient}</strong>
          </Typography>
        )}
        {status === 'pending' && (
          <Alert severity='info' sx={{ width: '100%', maxWidth: 680, textAlign: 'left' }}>
            Die Buchung ist gespeichert. Der Status aktualisiert sich automatisch, sobald die Mail
            zugestellt wurde oder die Zustellung fehlschlägt.
          </Alert>
        )}
        {status === 'pending' && mailStatusPollCount >= 22 && (
          <Alert severity='warning' sx={{ width: '100%', maxWidth: 680, textAlign: 'left' }}>
            Die Rückmeldung dauert ungewöhnlich lange. Du kannst den Schritt überspringen oder den
            Status manuell prüfen – der Abgleich läuft im Hintergrund weiter.
          </Alert>
        )}
        {canCorrectMail && (
          <Box sx={{ width: '100%', maxWidth: 560 }}>
            <TextField
              label='E-Mail-Adresse korrigieren'
              type='email'
              value={correctedMail}
              onChange={event => {
                setCorrectedMail(event.target.value);
                setMailCorrectionError('');
              }}
              onBlur={() => setMailCorrectionError(validateMailInput(correctedMail))}
              error={Boolean(mailCorrectionError)}
              helperText={mailCorrectionError || 'Die Bestätigung wird nach dem Speichern erneut versendet.'}
              fullWidth
            />
          </Box>
        )}
      </Stack>
    );
  };

  // Cart overview panel: lists the parked reservations ("Warenkorb") as cards with
  // date, person count, booked packages/upsells and amounts so the staff member
  // always sees what completing the booking covers.
  const renderCartPanel = () => {
    if (cartReservations.length === 0) return null;
    const cartTotal = cartReservations.reduce(
      (sum, item) => sum + (Number(item.totalAmount) || 0),
      0
    );
    // The cart items are booked for the same group of people, so the header shows
    // the group size (largest person count of any item), not the sum.
    const cartPersonTotal = cartReservations.reduce(
      (max, item) => Math.max(max, Number(item.personCount) || 0),
      0
    );
    const formatEuro = value =>
      `${Number(value || 0)
        .toFixed(2)
        .replace('.', ',')} €`;
    const splitDateLabel = label => {
      const [date, time] = String(label || '').split(' ');
      return { date: date || '', time: time || '' };
    };

    return (
      <Box
        sx={{
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: theme => alpha(theme.palette.primary.main, 0.35),
          background: theme =>
            `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
              theme.palette.primary.main,
              0.03
            )} 100%)`,
          overflow: 'hidden',
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          spacing={1.25}
          sx={{ px: 2, pt: 1.5, pb: 1 }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: theme => alpha(theme.palette.primary.main, 0.15),
              color: 'primary.main',
            }}
          >
            <ShoppingCartOutlinedIcon fontSize='small' />
          </Box>
          <Typography variant='subtitle1' sx={{ fontWeight: 700, flex: 1 }}>
            Warenkorb
          </Typography>
          {cartPersonTotal > 0 && (
            <Chip
              size='small'
              color='primary'
              variant='outlined'
              icon={<PeopleAltOutlinedIcon />}
              label={`${cartPersonTotal} ${cartPersonTotal === 1 ? 'Person' : 'Personen'}`}
            />
          )}
          <Chip
            size='small'
            color='primary'
            variant='outlined'
            label={`${cartReservations.length} ${
              cartReservations.length === 1 ? 'weitere Buchung' : 'weitere Buchungen'
            }`}
          />
        </Stack>
        <Stack
          direction='row'
          spacing={1.5}
          useFlexGap
          flexWrap='wrap'
          sx={{ px: 2, pb: 1.5 }}
        >
          {cartReservations.map(item => {
            const { date, time } = splitDateLabel(item.dateLabel);
            // Every booked upgrade is listed with the count it is billed with, so
            // a package per participant reads "5× Maxi-Paket" rather than just its name.
            const upgradeLabels = [
              item.packageName
                ? `Paket: ${item.packageCount > 0 ? `${item.packageCount}× ` : ''}${
                    item.packageName
                  }`
                : null,
              ...(item.upsells || []).map(upsell =>
                upsell.count > 0 ? `${upsell.count}× ${upsell.name}` : upsell.name
              ),
            ].filter(Boolean);
            return (
              <Box
                key={item.tempReservationId}
                sx={{
                  flex: '1 1 240px',
                  maxWidth: { sm: 340 },
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  px: 1.75,
                  py: 1.25,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                }}
              >
                <Stack direction='row' alignItems='flex-start' spacing={1}>
                  <Typography variant='subtitle2' sx={{ fontWeight: 700, flex: 1 }} noWrap>
                    {item.entertainmentName}
                  </Typography>
                  <Tooltip title='Buchung bearbeiten'>
                    <span>
                      <IconButton
                        size='small'
                        sx={{ mt: -0.5 }}
                        disabled={isLoading}
                        aria-label={`${item.entertainmentName} bearbeiten`}
                        onClick={() => handleEditCartReservation(item)}
                      >
                        <EditOutlinedIcon fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title='Aus dem Warenkorb entfernen'>
                    <span>
                      <IconButton
                        size='small'
                        color='error'
                        sx={{ mt: -0.5, mr: -0.75 }}
                        disabled={isLoading}
                        aria-label={`${item.entertainmentName} aus dem Warenkorb entfernen`}
                        onClick={() => handleRemoveCartReservation(item)}
                      >
                        <DeleteOutlineIcon fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Stack direction='row' alignItems='center' spacing={1} flexWrap='wrap' useFlexGap>
                  {date && (
                    <Chip
                      size='small'
                      variant='outlined'
                      icon={<EventOutlinedIcon />}
                      label={time ? `${date} · ${time} Uhr` : date}
                      sx={{ borderColor: 'divider' }}
                    />
                  )}
                  {item.personCount > 0 && (
                    <Chip
                      size='small'
                      variant='outlined'
                      icon={<PeopleAltOutlinedIcon />}
                      label={`${item.personCount} Pers.`}
                      sx={{ borderColor: 'divider' }}
                    />
                  )}
                  <Typography
                    variant='subtitle2'
                    sx={{ fontWeight: 700, color: 'primary.main', ml: 'auto' }}
                  >
                    {formatEuro(item.totalAmount)}
                  </Typography>
                </Stack>
                {upgradeLabels.length > 0 && (
                  <Typography variant='caption' color='text.secondary'>
                    {upgradeLabels.join(' · ')}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
        <Divider />
        <Stack
          direction='row'
          alignItems='baseline'
          justifyContent='flex-end'
          spacing={1}
          sx={{ px: 2, py: 1 }}
        >
          <Typography variant='body2' color='text.secondary'>
            Zwischensumme Warenkorb
          </Typography>
          <Typography variant='h6' sx={{ fontWeight: 800 }}>
            {formatEuro(cartTotal)}
          </Typography>
        </Stack>
      </Box>
    );
  };

  // Persistent Step Components
  const renderContent = () => (
    <Box sx={{ width: '100%' }}>
      {currentStep !== 'mailDelivery' && renderCartPanel()}
      <Box sx={{ display: currentStep === 'booking' ? 'block' : 'none' }}>
        <Box
          key={bookingStepKey}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            minHeight: { md: '100%' },
          }}
        >
          <EntertainmentSelect
            branchId={branchId}
            entertainmentId={entertainmentId}
            branchLocked={cartReservations.length > 0}
          />
          {entertainmentError ? (
            // The error hides the whole date/person/time section and nothing
            // except changing branch/entertainment/product clears it, so a
            // single failed availability request would otherwise force the user
            // to close the modal and start over.
            <Alert
              severity='error'
              sx={{ mb: 2 }}
              action={
                <Button color='inherit' size='small' onClick={() => setEntertainmentError(null)}>
                  Erneut versuchen
                </Button>
              }
            >
              {entertainmentError.toString()}
            </Alert>
          ) : null}
          {selectedBranch &&
            selectedEntertainment &&
            selectedProduct &&
            !entertainmentError &&
            (resources.length === 0 || selectedResource) && (
              <>
                <DateSelect onDateSelect={handleDateSelect} onError={handleDateError} />
                {selectedDate && !isDayBookable && (
                  <Alert severity='warning' sx={{ mt: 2 }}>
                    Keine Kapazitäten an diesem Tag verfügbar.
                  </Alert>
                )}
                {selectedDate && isDayBookable && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, md: 4 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: { xs: 3, md: 4 },
                        alignItems: { xs: 'stretch', md: 'flex-start' },
                      }}
                    >
                      <Box sx={{ flex: 1, width: '100%', minHeight: { xs: 'auto', md: 'auto' } }}>
                        <PersonSelect
                          selectedDate={selectedDate}
                          selectedTime={selectedTime}
                          selectedDuration={selectedDuration}
                          onPersonSelect={handlePersonSelect}
                        />
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          width: '100%',
                          minHeight: { xs: 'auto', md: 'auto' },
                          mt: { md: -2 },
                        }}
                      >
                        <DurationSelect
                          selectedDate={selectedDate}
                          onDurationSelect={handleDurationSelect}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ width: '100%' }}>
                      <TimeSelect selectedDate={selectedDate} onTimeSelect={handleTimeSelect} />
                    </Box>
                  </Box>
                )}
              </>
            )}
        </Box>
      </Box>
      <Box sx={{ display: currentStep === 'packages' ? 'block' : 'none' }}>
        <PackageAndUpsellSelect isLoading={isLoading} />
      </Box>
      <Box sx={{ display: currentStep === 'customer' ? 'block' : 'none' }}>
        <CustomerData
          ref={customerDataRef}
          onWalkInSubmissionSelected={id => {
            selectedWalkInSubmissionIdRef.current = id;
          }}
        />
      </Box>
      <Box sx={{ display: currentStep === 'overview' ? 'block' : 'none' }}>
        <Overview
          ref={overviewRef}
          tempReservationId={tempReservationId}
          reservationCartSummary={reservationCartSummary}
        />
      </Box>
      <Box sx={{ display: currentStep === 'mailDelivery' ? 'block' : 'none' }}>
        {renderMailDeliveryContent()}
      </Box>
    </Box>
  );

  const renderButtons = () => {
    switch (currentStep) {
      case 'booking':
        return (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent='flex-end'
            sx={{ width: '100%' }}
          >
            <Button
              variant='outlined'
              color='error'
              onClick={handleCloseRequest}
              disabled={isLoading}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Buchung abbrechen
            </Button>
            <Button
              variant='outlined'
              color='success'
              onClick={handleGoToPackages}
              disabled={isLoading}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isLoading && <CircularProgress size={16} sx={{ mr: 1 }} color='inherit' />}
              {isLoading
                ? 'Reservierung wird angelegt …'
                : packages.length === 0 && upsells.length === 0
                  ? 'Kundendaten eingeben'
                  : 'Pakete auswählen'}
            </Button>
          </Stack>
        );
      case 'packages':
        return (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent='space-between'
            alignItems='center'
            sx={{ width: '100%' }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant='subtitle1'>
                Gesamtsumme: {calculateTotalAmount().toFixed(2).replace('.', ',')}€
              </Typography>
            </Box>
            <Button
              variant='outlined'
              color='error'
              onClick={handleCloseRequest}
              disabled={isLoading}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Buchung abbrechen
            </Button>
            <Button
              variant='outlined'
              sx={{
                color: 'grey.500',
                borderColor: 'grey.500',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  borderColor: 'grey.300',
                },
              }}
              onClick={async () => {
                if (tempReservationId) {
                  try {
                    await invalidateTemporaryReservation(tempReservationId);
                    setTempReservationId(null);
                    setReservationCartSummary(null);
                  } catch (error) {
                    toast.error('Fehler beim Stornieren der Reservierung');
                    console.error('Error invalidating temporary reservation:', error);
                  }
                }
                setCurrentStep('booking');
              }}
            >
              Zurück
            </Button>
            <Button
              variant='outlined'
              color='primary'
              onClick={handleAddAnotherEntertainment}
              disabled={isLoading || !tempReservationId}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Weiteres Entertainment dazubuchen
            </Button>
            <Button
              variant='outlined'
              color='success'
              onClick={handleGoToCustomer}
              disabled={isPackagesLoading || isUpsellsLoading}
              sx={{
                borderWidth: '1px',
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Kundendaten eingeben
            </Button>
          </Stack>
        );
      case 'customer':
        return (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent='flex-end'
            sx={{ width: '100%' }}
          >
            <Button
              variant='outlined'
              color='error'
              onClick={handleCloseRequest}
              disabled={isLoading}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Buchung abbrechen
            </Button>
            <Button
              variant='outlined'
              sx={{
                color: 'grey.500',
                borderColor: 'grey.500',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  borderColor: 'grey.300',
                },
              }}
              onClick={async () => {
                if (packages.length === 0 && upsells.length === 0) {
                  await invalidateReservationIfAny();
                  setCurrentStep('booking');
                  return;
                }
                setCurrentStep('packages');
              }}
            >
              Zurück
            </Button>
            <Button
              variant='outlined'
              color='primary'
              onClick={handleAddAnotherEntertainment}
              disabled={isLoading || !tempReservationId}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Weiteres Entertainment dazubuchen
            </Button>
            <Button
              variant='outlined'
              color='success'
              onClick={() => {
                // Commit the latest field values and validate them so the user
                // always gets a concrete message about what is missing/invalid.
                const result = customerDataRef.current?.validateAndCommit?.();
                if (result && !result.isValid) {
                  toast.error(`Bitte vervollständige die Kundendaten: ${result.messages.join(', ')}`);
                  return;
                }
                // Persist customer data to localStorage for reuse within 15 minutes
                persistLastCustomerData(result?.data);
                calculateLineItems();
                setCurrentStep('overview');
              }}
              disabled={isLoading}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Weiter
            </Button>
          </Stack>
        );
      case 'overview':
        return (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent='flex-end'
            sx={{ width: '100%' }}
          >
            <Button
              variant='outlined'
              color='error'
              onClick={handleCloseRequest}
              disabled={isLoading}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Buchung abbrechen
            </Button>
            <Button
              variant='outlined'
              sx={{
                color: 'grey.500',
                borderColor: 'grey.500',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  borderColor: 'grey.300',
                },
              }}
              onClick={() => setCurrentStep('customer')}
            >
              Zurück
            </Button>
            <Button
              variant='outlined'
              color='primary'
              onClick={handleAddAnotherEntertainment}
              disabled={isLoading}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Weiteres Entertainment dazubuchen
            </Button>
            <Button
              variant='outlined'
              color={isRequestOnly && !hasConfirmPendingRight ? undefined : 'success'}
              onClick={handleCompleteBooking}
              disabled={isLoading || !areSpecialLineItemsComplete()}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderWidth: '1px',
              }}
            >
              <LockIconWrapper right='internalBooking:create'>
                {isLoading
                  ? 'Buchung wird abgeschlossen...'
                  : cartReservations.length > 0
                    ? `Warenkorb abschließen (${cartReservations.length + 1} Buchungen)`
                    : isRequestOnly && !hasConfirmPendingRight
                      ? 'Anfrage stellen'
                      : 'Buchung abschließen'}
              </LockIconWrapper>
            </Button>
          </Stack>
        );
      case 'mailDelivery': {
        const status = mailDeliveryStatus?.status || 'pending';
        const canRetry = status === 'failed' || status === 'bounced';
        const canRefresh = status === 'pending' && mailStatusPollCount >= 22;
        const isDone = ['delivered', 'skipped'].includes(status);
        return (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent='flex-end'
            sx={{ width: '100%' }}
          >
            <Button
              variant='outlined'
              sx={{ width: { xs: '100%', sm: 'auto' } }}
              onClick={finishCreatedBookingFlow}
              disabled={isResendingMail}
            >
              {isDone ? 'Fertig' : 'Schritt überspringen'}
            </Button>
            {canRefresh && (
              <Button
                variant='outlined'
                onClick={refreshMailDeliveryStatus}
                disabled={isMailStatusLoading || isResendingMail}
                startIcon={isMailStatusLoading ? <CircularProgress size={18} /> : null}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Status erneut prüfen
              </Button>
            )}
            {canRetry && (
              <Button
                variant='contained'
                color='success'
                onClick={handleRetryMailDelivery}
                disabled={isResendingMail}
                startIcon={
                  isResendingMail ? <CircularProgress size={18} color='inherit' /> : <SendIcon />
                }
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Erneut versenden
              </Button>
            )}
          </Stack>
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      {/*
        Escape reaches onClose like a backdrop click does, so it runs through
        the same discard confirmation instead of dropping a half-entered
        booking (and its temporary reservation) without asking.
      */}
      <Modal open={open} onClose={handleCloseRequest}>
        <Box
          sx={{
            // Anchored to the top instead of vertically centred: the wizard
            // loads its data in a cascade, and a centred modal grew in both
            // directions, so every response slid the whole dialog upwards while
            // the user was already reading it.
            top: '2.5vh',
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: '95%', sm: '90%', md: '85%' },
            maxWidth: '1400px',
            maxHeight: { xs: '95vh', sm: '90vh' },
            overflowY: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 2,
            position: 'relative',
          }}
        >
          {currentStep === 'customer' && <CopyCustomerDataSelect />}
          {renderContent()}
          <Stack direction='column' spacing={2} sx={{ mt: { xs: 3, md: 4 } }}>
            {renderButtons()}
          </Stack>
        </Box>
      </Modal>

      <Dialog
        open={showRequestOnlyConfirm}
        onClose={() => setShowRequestOnlyConfirm(false)}
        aria-labelledby='request-only-dialog'
      >
        <DialogTitle id='request-only-dialog'>Buchung nur auf Anfrage</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Diese Buchung wird <strong>unter Vorbehalt</strong> angelegt und ist nicht garantiert.
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Du kannst die Buchungsanfrage speichern oder (falls berechtigt) direkt bestätigen.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRequestOnlyConfirm(false)} color='error'>
            Abbrechen
          </Button>
          <Button
            onClick={async () => {
              setShowRequestOnlyConfirm(false);
              await performCompleteBooking(false);
            }}
            color='primary'
          >
            <LockIconWrapper right='internalBooking:create'>Anfrage stellen</LockIconWrapper>
          </Button>
          {hasConfirmPendingRight ? (
            <Button
              onClick={async () => {
                setShowRequestOnlyConfirm(false);
                await performCompleteBooking(true);
              }}
              color='success'
              variant='contained'
            >
              <LockIconWrapper right='internalBooking:special:confirmpending'>
                Direkt bestätigen
              </LockIconWrapper>
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={showConfirmClose}
        onClose={handleCancelClose}
        aria-labelledby='confirm-close-dialog'
      >
        <DialogTitle id='confirm-close-dialog'>Buchung abbrechen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchtest du die Buchung wirklich abbrechen? Alle eingegebenen Daten gehen verloren.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose} color='primary'>
            Weiter buchen
          </Button>
          <Button onClick={handleConfirmClose} color='error' variant='contained'>
            Abbrechen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
